import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StreamDto } from '@sflx/shared';
import { catalog, type StreamQueryParams } from '../lib/api';

export interface StreamFeed {
  items: StreamDto[];
  /** Only the first page carries a total; null until it arrives. */
  total: number | null;
  loading: boolean;
  loadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
  /** Optimistic local edit, e.g. a favorite toggle. */
  patch: (xtreamId: number, changes: Partial<StreamDto>) => void;
}

const EMPTY: StreamDto[] = [];

/**
 * Cursor-paged catalog reads.
 *
 * Every response is checked against a generation counter before it is applied.
 * Without that, switching category while a page is in flight appends the old
 * category's rows to the new list — the classic "wrong channels under the right
 * heading" bug, and it is invisible on a fast connection.
 */
export function useStreamFeed(accountId: string | null, query: StreamQueryParams | null): StreamFeed {
  const key = accountId && query ? `${accountId}|${JSON.stringify(query)}` : '';

  const [items, setItems] = useState<StreamDto[]>(EMPTY);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(true);

  const generation = useRef(0);
  const latest = useRef<{ accountId: string | null; query: StreamQueryParams | null }>({ accountId, query });
  latest.current = { accountId, query };

  const cursorRef = useRef<string | null>(null);
  cursorRef.current = cursor;
  const busy = useRef(false);

  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const { accountId: id, query: params } = latest.current;
    if (!id || !params) {
      setItems(EMPTY);
      setTotal(null);
      setCursor(null);
      setExhausted(true);
      setLoading(false);
      return;
    }

    const mine = (generation.current += 1);
    busy.current = true;
    setLoading(true);
    setError(null);

    catalog
      .streams(id, params)
      .then((page) => {
        if (generation.current !== mine) return;
        setItems(page.items);
        setTotal(page.total ?? null);
        setCursor(page.nextCursor);
        setExhausted(page.nextCursor === null);
      })
      .catch((caught: Error) => {
        if (generation.current !== mine) return;
        setItems(EMPTY);
        setError(caught);
        setExhausted(true);
      })
      .finally(() => {
        if (generation.current !== mine) return;
        busy.current = false;
        setLoading(false);
      });
    // `key` is the serialized identity of (accountId, query); depending on the
    // object itself would refetch on every render.
  }, [key, nonce]);

  const loadMore = useCallback(() => {
    const { accountId: id, query: params } = latest.current;
    const next = cursorRef.current;
    if (!id || !params || !next || busy.current) return;

    const mine = generation.current;
    busy.current = true;
    setLoadingMore(true);

    catalog
      .streams(id, { ...params, cursor: next })
      .then((page) => {
        if (generation.current !== mine) return;
        setItems((previous) => previous.concat(page.items));
        setCursor(page.nextCursor);
        setExhausted(page.nextCursor === null);
      })
      .catch((caught: Error) => {
        if (generation.current !== mine) return;
        setError(caught);
      })
      .finally(() => {
        if (generation.current !== mine) return;
        busy.current = false;
        setLoadingMore(false);
      });
  }, []);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const patch = useCallback((xtreamId: number, changes: Partial<StreamDto>) => {
    setItems((previous) =>
      previous.map((item) => (item.id === xtreamId ? { ...item, ...changes } : item)),
    );
  }, []);

  return useMemo(
    () => ({
      items,
      total,
      loading,
      loadingMore,
      error,
      hasMore: !exhausted,
      loadMore,
      reload,
      patch,
    }),
    [items, total, loading, loadingMore, error, exhausted, loadMore, reload, patch],
  );
}
